import { useEffect, useId, useRef, useState } from 'react';
import { X, Trash2 } from 'lucide-react';
import { getEloLevel, ELO_LEVELS } from '../../engine';
import { useT } from '../../i18n';
import { Dialog } from '../ui';
import { safeId } from '../ui/labelledControl';

export interface PlayerEditModalProps {
  /** 'create' opens a blank form (with duplicate-name guard); 'edit' pre-fills from `player`. */
  mode: 'create' | 'edit';
  /** The player being edited; read defensively (name/email/gender/elo). Required in 'edit' mode. */
  player?: any;
  /** Closes the modal without saving. */
  onClose: () => void;
  /** Persists the edited player; awaited (handleSave is async). */
  onSave: (info: {
    name: string;
    firstName: string;
    lastName: string;
    email: string | undefined;
    gender: string;
    /** The STARTING rating, on create only — undefined when editing (#197). */
    elo: number | undefined;
  }) => Promise<void> | void;
  /** Deletes the player (only wired in 'edit' mode behind a confirm step). */
  onDelete?: () => void;
  /** Pre-fills the name field on create (e.g. typed-in search text). */
  initialName?: string;
  /** Existing players, used for the create-mode duplicate-name check. */
  existingPlayers?: any[];
  /** When set, the duplicate banner offers reusing the matched player instead of creating a new one. */
  onUseExisting?: ((dupe: any) => void) | null;
  /** When set, locks the gender toggle to this value (e.g. a gendered class registration rule). */
  lockGender?: string | null;
}

/**
 * Player create/edit modal — name, e-mail, gender and ELO (with the 9-level quick picker).
 * Extracted verbatim from the original monolith (legacy/App.jsx); markup/logic unchanged so behaviour is identical.
 */
export function PlayerEditModal({
  mode,
  player,
  onClose,
  onSave,
  onDelete,
  initialName = '',
  existingPlayers = [],
  onUseExisting = null,
  lockGender = null,
}: PlayerEditModalProps) {
  const t = useT();
  // Split the existing name into first/last name for editing
  const splitName = (full) => {
    if (!full) return { first: '', last: '' };
    const parts = full.trim().split(/\s+/);
    if (parts.length === 1) return { first: parts[0], last: '' };
    return { first: parts[0], last: parts.slice(1).join(' ') };
  };

  const initial = splitName(player?.name || (mode === 'create' ? initialName : ''));
  const [firstName, setFirstName] = useState(initial.first);
  const [lastName, setLastName] = useState(initial.last);
  const [email, setEmail] = useState(player?.email || '');
  const [gender, setGender] = useState(player?.gender || lockGender || 'M');
  const [elo, setElo] = useState(player?.elo || 1500);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // NAMES AND FOCUS, now that this is a `Dialog` (#900). Every visible label
  // gets the id of the control it names. The first-name field used
  // `autoFocus`, which the dialog's own focus move would override, so it is
  // `initialFocus` instead. The delete confirmation REPLACES the form inside
  // the same dialog, which unmounts the button that opened it, so focus would
  // fall to <body>: it moves to «Avbryt» on the way in, and back to «Slett» on
  // the way out.
  const uid = safeId('player-edit', useId());
  const ids = {
    title: `${uid}-title`,
    first: `${uid}-first`,
    last: `${uid}-last`,
    email: `${uid}-email`,
    emailError: `${uid}-email-error`,
    emailHint: `${uid}-email-hint`,
    gender: `${uid}-gender`,
    genderHint: `${uid}-gender-hint`,
    elo: `${uid}-elo`,
    invalid: `${uid}-invalid`,
    selfManaged: `${uid}-self-managed`,
  };
  const firstRef = useRef<HTMLInputElement | null>(null);
  const confirmCancelRef = useRef<HTMLButtonElement | null>(null);
  const deleteRef = useRef<HTMLButtonElement | null>(null);
  const wasConfirming = useRef(false);
  useEffect(() => {
    if (confirmDelete) confirmCancelRef.current?.focus();
    else if (wasConfirming.current) deleteRef.current?.focus();
    wasConfirming.current = confirmDelete;
  }, [confirmDelete]);

  // ── THE STARTING RATING IS THE CLUB'S; A CORRECTION IS NOT (#197) ──
  //
  // Creating a player sets their first rating, and a club genuinely knows
  // roughly how good a new player is — `POST /players` takes it, and nothing
  // about that changed. CHANGING an existing rating is a different act on a
  // table with no tenant column: it is `POST /players/:id/elo-adjustment`,
  // which writes an audit row and now asks for a platform capability. So the
  // picker is offered on create and the rating is read-only on edit.
  const ratingIsEditable = mode === 'create';

  // ── A PLAYER WITH AN ACCOUNT MANAGES THEIR OWN IDENTITY (#796) ──────
  //
  // Decided 2026-10-23: the player edits their name, address, phone and gender
  // themself, or StarPoint support does; a club is refused
  // (`player_self_managed`). The form still OPENS — the player is not hidden,
  // and the club sees who they are — but the identity fields are read-only and
  // the form says why. Nothing in this form is the club's to change for such a
  // player, so it cannot be saved either.
  const selfManaged = mode === 'edit' && player?.selfManaged === true;

  // Validation
  const trimmedFirst = firstName.trim();
  const trimmedLast = lastName.trim();
  const trimmedEmail = email.trim();
  const emailValid = trimmedEmail === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);
  // Duplicate check (only on create): does a player with the same full name already exist?
  const fullNameTrim = `${trimmedFirst} ${trimmedLast}`.trim();
  const dupe =
    mode === 'create' && fullNameTrim.length > 0
      ? (existingPlayers || []).find(
          (p) => (p.name || '').trim().toLowerCase() === fullNameTrim.toLowerCase(),
        )
      : null;
  const isValid =
    trimmedFirst.length > 0 &&
    trimmedLast.length > 0 &&
    emailValid &&
    // ONLY WHERE THE RATING IS AN INPUT. On edit it is the stored value, and
    // holding a name change hostage to a rating this box cannot change would
    // make an out-of-range player uneditable.
    (!ratingIsEditable || (elo >= 100 && elo <= 4000)) &&
    !dupe &&
    !selfManaged;
  const title =
    mode === 'create' ? t('players.playerEdit.createTitle') : t('players.playerEdit.editTitle');

  const handleSave = async () => {
    if (!isValid) return;
    const fullName = `${trimmedFirst} ${trimmedLast}`;
    await onSave({
      name: fullName,
      firstName: trimmedFirst,
      lastName: trimmedLast,
      email: trimmedEmail || undefined,
      gender,
      // ABSENT ON EDIT, not «the same number again»: `usePlayers.updatePlayer`
      // reads a rating in the payload as a request to move it by hand, and
      // the stored value may be null — in which case sending 1500 back would
      // ask for exactly the correction this screen no longer offers.
      elo: ratingIsEditable ? parseInt(elo, 10) : undefined,
    });
  };

  // Check which level is selected (based on the current ELO value)
  const currentLevel = getEloLevel(parseInt(elo, 10) || 1500);

  return (
    <Dialog
      labelledBy={ids.title}
      onClose={onClose}
      showClose={false}
      initialFocus={firstRef}
      backdropClassName=""
      backdropStyle={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        background: 'rgba(15, 22, 17, 0.55)',
      }}
      // The panel: maxHeight is set to calc(100dvh - 100px) to avoid the sticky
      // Padel24 banner (z-30) clipping the modal header. The banner is about
      // 56px tall + a little padding breathing room. dvh accounts for mobile
      // chrome. display:flex + column makes the header and footer sticky, while
      // the content in between is scrollable. This way the user always sees
      // Save/Cancel.
      className=""
      style={{
        background: '#ffffff',
        maxWidth: 440,
        width: '100%',
        maxHeight: 'calc(100dvh - 100px)',
        marginTop: 80,
        borderRadius: 16,
        overflow: 'hidden',
        boxShadow: '0 25px 60px -10px rgba(0,0,0,0.4)',
        border: '1px solid #e7eae3',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          padding: '14px 20px',
          borderBottom: '1px solid #e7eae3',
          background: '#fafbf7',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}
      >
        <h2 id={ids.title} className="font-display text-lg font-semibold leading-tight">
          {title}
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('ui.dialog.close')}
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: '#4a544c',
            padding: 4,
          }}
        >
          <X size={16} />
        </button>
      </div>

      {confirmDelete ? (
        <div style={{ padding: 20, overflowY: 'auto' }}>
          <p className="text-sm text-ink-2 mb-4 leading-relaxed">
            {t('players.playerEdit.confirmDelete.before')} <strong>{player.name}</strong>{' '}
            {t('players.playerEdit.confirmDelete.after')}
          </p>
          <div className="flex gap-2 justify-end">
            <button
              ref={confirmCancelRef}
              type="button"
              onClick={() => setConfirmDelete(false)}
              className="btn-ghost"
            >
              {t('players.common.cancel')}
            </button>
            <button
              type="button"
              onClick={onDelete}
              className="px-4 py-2 rounded-full text-sm font-semibold text-white hover:opacity-90"
              style={{ background: '#f43f5e' }}
            >
              {t('players.playerEdit.deleteConfirm')}
            </button>
          </div>
        </div>
      ) : (
        <>
          <div style={{ padding: 20, overflowY: 'auto', flex: '1 1 auto' }} className="space-y-4">
            {selfManaged && (
              <p
                id={ids.selfManaged}
                role="note"
                className="text-[12px] text-ink-2 leading-snug p-2.5 rounded-lg border border-line bg-bg-soft"
                data-testid="player-self-managed"
              >
                {t('players.playerEdit.selfManaged')}
              </p>
            )}
            {/* First name + last name */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label
                  htmlFor={ids.first}
                  className="block text-xs font-mono uppercase tracking-wider text-ink-3 font-semibold mb-1.5"
                >
                  {t('players.playerEdit.firstName')}
                </label>
                <input
                  ref={firstRef}
                  id={ids.first}
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder={t('players.playerEdit.firstNamePlaceholder')}
                  className="input w-full text-sm"
                  disabled={selfManaged}
                  aria-describedby={selfManaged ? ids.selfManaged : undefined}
                />
              </div>
              <div>
                <label
                  htmlFor={ids.last}
                  className="block text-xs font-mono uppercase tracking-wider text-ink-3 font-semibold mb-1.5"
                >
                  {t('players.playerEdit.lastName')}
                </label>
                <input
                  id={ids.last}
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder={t('players.playerEdit.lastNamePlaceholder')}
                  className="input w-full text-sm"
                  disabled={selfManaged}
                  aria-describedby={selfManaged ? ids.selfManaged : undefined}
                />
              </div>
            </div>

            {dupe && (
              <div
                className="p-2.5 rounded-lg border"
                style={{ borderColor: '#f59e0b', background: '#fffbeb' }}
              >
                <p className="text-[12px] text-ink-1 leading-snug">
                  {t('players.common.dupe.before')} <strong>{dupe.name}</strong>
                  {dupe.email ? '' : t('players.common.dupe.ghost')}
                  {t('players.common.dupe.after')}
                </p>
                {onUseExisting && (
                  <button
                    type="button"
                    onClick={() => onUseExisting(dupe)}
                    className="mt-2 text-xs font-semibold px-3 py-1.5 rounded-lg text-white"
                    style={{ background: '#f59e0b' }}
                  >
                    {t('players.common.useExisting')}
                  </button>
                )}
              </div>
            )}

            {/* E-mail */}
            <div>
              <label
                htmlFor={ids.email}
                className="block text-xs font-mono uppercase tracking-wider text-ink-3 font-semibold mb-1.5"
              >
                {t('players.playerEdit.email')}{' '}
                <span className="text-ink-4 normal-case font-normal tracking-normal">
                  {t('players.playerEdit.emailHint')}
                </span>
              </label>
              <input
                id={ids.email}
                type="email"
                aria-invalid={!emailValid || undefined}
                aria-describedby={
                  selfManaged
                    ? ids.selfManaged
                    : !emailValid
                      ? ids.emailError
                      : mode === 'create'
                        ? ids.emailHint
                        : undefined
                }
                disabled={selfManaged}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('players.playerEdit.emailPlaceholder')}
                className={`input w-full text-sm ${!emailValid ? 'border-rose' : ''}`}
                style={!emailValid ? { borderColor: '#f43f5e' } : undefined}
              />
              {!emailValid && (
                <div id={ids.emailError} className="text-[10px] text-rose mt-1 font-mono">
                  {t('players.playerEdit.emailInvalid')}
                </div>
              )}
              {mode === 'create' && emailValid && (
                <div id={ids.emailHint} className="text-[10px] text-ink-3 mt-1">
                  {trimmedEmail
                    ? t('players.playerEdit.emailSet')
                    : t('players.playerEdit.emailEmpty')}
                </div>
              )}
            </div>

            {/* Gender */}
            <div
              role="group"
              aria-labelledby={ids.gender}
              aria-describedby={lockGender ? ids.genderHint : undefined}
            >
              <span
                id={ids.gender}
                className="block text-xs font-mono uppercase tracking-wider text-ink-3 font-semibold mb-1.5"
              >
                {t('players.common.gender')}
              </span>
              <div className="flex items-center gap-2">
                {[
                  { id: 'M', label: t('players.playerEdit.maleOption') },
                  { id: 'F', label: t('players.playerEdit.femaleOption') },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    aria-pressed={gender === opt.id}
                    onClick={() => {
                      if (!lockGender) setGender(opt.id);
                    }}
                    disabled={selfManaged || (!!lockGender && lockGender !== opt.id)}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm transition-colors border ${
                      gender === opt.id
                        ? 'bg-ink text-white border-ink'
                        : 'bg-paper text-ink-2 border-line hover:border-ink-4'
                    } ${!!lockGender && lockGender !== opt.id ? 'opacity-30 cursor-not-allowed' : ''}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {lockGender && (
                <div id={ids.genderHint} className="text-[10px] text-ink-3 mt-1">
                  {t('players.playerEdit.genderLocked')}
                </div>
              )}
            </div>

            {/* ELO with level quick-buttons */}
            <div>
              <div className="flex items-baseline justify-between mb-1.5">
                {/* A TITLE FOR SEVERAL CONTROLS (the level buttons, the number,
                      the slider) — or for a read-only value on edit — so it is
                      a named span that each of them points at, not a <label>. */}
                <span
                  id={ids.elo}
                  className="text-xs font-mono uppercase tracking-wider text-ink-3 font-semibold"
                >
                  {t('players.playerEdit.eloRating')}
                </span>
                <span
                  className="text-[10px] font-mono font-bold px-2 py-0.5 rounded uppercase tracking-wider"
                  style={{ background: currentLevel.color, color: currentLevel.text }}
                >
                  {currentLevel.name}
                </span>
              </div>

              {/* THE PICKER AND THE SLIDER ARE FOR A NEW PLAYER (#197).
                    On edit the rating is shown instead: it moves through
                    recorded results and the engine's arithmetic, and a manual
                    correction is a Starpoint operation. */}
              {ratingIsEditable ? (
                <>
                  {/* Quick-buttons for 9 levels — 3 columns */}
                  <div
                    className="grid grid-cols-3 gap-1.5 mb-3"
                    role="group"
                    aria-labelledby={ids.elo}
                  >
                    {ELO_LEVELS.map((lvl) => {
                      const isActive = currentLevel.id === lvl.id;
                      return (
                        <button
                          key={lvl.id}
                          type="button"
                          aria-pressed={isActive}
                          onClick={() => setElo(lvl.midpoint)}
                          // THE CHOICE IS MARKED, NOT THE REST FADED (#1295,
                          // owner decision 2026-11-02): unselected cards were
                          // drawn at opacity 0.7, which took every level's
                          // text below 4.5:1. Each card is at full colour now,
                          // and the chosen one carries an ink ring (17:1 on
                          // the dialog) beside its border and shadow.
                          className={`px-1.5 py-2 rounded-md text-xs font-medium transition-all border text-left ${
                            isActive
                              ? 'shadow-sm ring-2 ring-ink ring-offset-1 ring-offset-paper'
                              : ''
                          }`}
                          style={{
                            background: lvl.color,
                            color: lvl.text,
                            borderColor: isActive ? lvl.text : 'transparent',
                            borderWidth: isActive ? '1.5px' : '1px',
                          }}
                          title={lvl.description}
                        >
                          {/* Secondary by size and weight, not by fading: at
                              0.7 / 0.8 these 9 px lines were 2.8–3.45:1. */}
                          <div className="font-mono text-[9px] font-medium">
                            {t('players.playerEdit.levelBadge', { level: lvl.level })}
                          </div>
                          <div className="text-[11px] font-bold leading-tight mt-0.5">
                            {lvl.name}
                          </div>
                          <div className="font-mono text-[9px] mt-0.5">
                            {lvl.max >= 9999 ? `${lvl.min}+` : `${lvl.min}–${lvl.max - 1}`}
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {/* Fine-tuning — slider + number */}
                  <div className="flex items-center gap-3">
                    <input
                      aria-labelledby={ids.elo}
                      type="number"
                      value={elo}
                      onChange={(e) => setElo(e.target.value)}
                      min="100"
                      max="4000"
                      step="25"
                      className="input w-24 text-sm font-mono"
                    />
                    <input
                      aria-labelledby={ids.elo}
                      type="range"
                      value={elo}
                      onChange={(e) => setElo(e.target.value)}
                      min={ELO_LEVELS[0].min}
                      max={ELO_LEVELS[ELO_LEVELS.length - 1].midpoint + 200}
                      step="25"
                      className="flex-1"
                    />
                  </div>
                </>
              ) : (
                <div className="mb-3">
                  <div className="text-lg font-mono font-semibold">{elo}</div>
                  <div className="text-[11px] text-ink-3 leading-relaxed mt-1">
                    {t('players.playerEdit.ratingFollowsResults')}
                  </div>
                </div>
              )}
              {/* Description + skills for the selected level */}
              <div
                className="mt-3 p-3 rounded-md border"
                style={{
                  background: currentLevel.color,
                  borderColor: currentLevel.text,
                }}
              >
                <div
                  className="text-[11px] font-semibold mb-1"
                  style={{ color: currentLevel.text }}
                >
                  {currentLevel.description}
                </div>
                <ul className="text-[10px] space-y-0.5 mt-1.5" style={{ color: currentLevel.text }}>
                  {currentLevel.skills.map((s, i) => (
                    <li key={i} className="flex gap-1.5">
                      <span className="opacity-60">•</span>
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div
            style={{
              padding: '12px 20px',
              borderTop: '1px solid #e7eae3',
              background: '#fafbf7',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              flexShrink: 0,
            }}
          >
            {/* Show which fields are missing so the user understands why
                  the button is disabled. Otherwise it stays silent and confusing. */}
            {!isValid && !selfManaged && (
              <div
                id={ids.invalid}
                className="text-[11px] text-rose flex items-center gap-1 leading-tight"
                style={{ color: '#b91c1c' }}
              >
                <span aria-hidden="true">⚠</span>
                <span>
                  {!trimmedFirst
                    ? t('players.playerEdit.missingFirstName')
                    : !trimmedLast
                      ? t('players.playerEdit.missingLastName')
                      : !emailValid
                        ? t('players.playerEdit.emailInvalidOrEmpty')
                        : elo < 100 || elo > 4000
                          ? t('players.playerEdit.eloRange')
                          : t('players.playerEdit.cannotSave')}
                </span>
              </div>
            )}

            <div className="flex justify-between items-center gap-8">
              {mode === 'edit' && onDelete ? (
                <button
                  ref={deleteRef}
                  type="button"
                  onClick={() => setConfirmDelete(true)}
                  className="text-xs font-semibold text-rose hover:underline flex items-center gap-1"
                >
                  <Trash2 size={12} /> {t('players.playerEdit.delete')}
                </button>
              ) : (
                <span />
              )}

              <div className="flex gap-2">
                <button type="button" onClick={onClose} className="btn-ghost">
                  {t('players.common.cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={!isValid}
                  // WHY IT IS DISABLED, attached to the button that is.
                  aria-describedby={
                    selfManaged ? ids.selfManaged : !isValid ? ids.invalid : undefined
                  }
                  className={`btn-primary ${!isValid ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {mode === 'create' ? t('players.playerEdit.create') : t('players.common.save')}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </Dialog>
  );
}
